import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Icon } from '../components/Icon.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { SegmentedControl } from '../components/ui/SegmentedControl.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { ListItem } from '../components/ui/ListItem.js';
import { ListToolbar } from '../components/ui/ListToolbar.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Button } from '../components/ui/Button.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { formatMoney } from '../lib/money.js';
import { useContacts } from '../hooks/useContacts.js';
import { EstimateDetail, ESTIMATE_STATUS_TONES } from '../components/billing/EstimateDetail.js';
import { useComposeEstimateSend } from '../hooks/useEstimateOutreach.js';
import { useComposeInvoiceSend, useComposeInvoiceReminder, useMarkInvoicePaid } from '../hooks/useInvoiceOutreach.js';
import { displayInvoiceStatus } from '../lib/billingStatus.js';
import { InvoiceDetail } from '../components/billing/InvoiceDetail.js';
import { BillingClaudePanel } from '../components/billing/BillingClaudePanel.js';
import { CreateEstimateModal } from '../components/billing/CreateEstimateModal.js';
import {
  useEstimates, useEstimate, useInvoices, useInvoice, useAccounts,
  useBulkDeleteEstimates, useBulkDeleteInvoices, useConvertEstimate, useMarkEstimateAccepted,
  type Estimate, type Invoice,
} from '../hooks/useBilling.js';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(value: number): string {
  return new Date(value).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

type DocType = 'estimate' | 'invoice';
type DocRow = (Estimate & { docType: 'estimate' }) | (Invoice & { docType: 'invoice' });

const ALL_BANDS = new Set(['today', 'yesterday', 'this_week', 'this_month', 'older_2_3_months', 'older']);

// ── Detail header ──────────────────────────────────────────────────────────────
// Copied verbatim from ReportsPage.tsx's DetailPanel "Action row" (not a shared
// component in that file either — it's inline JSX there too, so this replicates
// the same markup/tokens rather than importing a component that doesn't exist as
// an extracted unit). Same title typography (--evo-fs-body-lg / --evo-fw-bold /
// --evo-fg-1), same type-badge span, same Pill usage, same action-row shape with
// plain <Button> (this row lives outside any DetailSection, so REGLA #15's
// SectionHeaderAction-only rule — which is specific to DetailSection's titleRight
// — doesn't apply here; Reports' own header uses plain Button for the same reason).
// Reports has no separate "client" element in this header — for report types like
// web_audit, the subject is baked directly into report.title ("Web audit — domain.com").
// Billing mirrors that by composing the title the same way: "{folio} — {client}".

function DetailHeader({ title, typeLabel, status, statusNamespace = 'estimateStatus', actions }: { title: string; typeLabel: string; status: string; statusNamespace?: 'estimateStatus' | 'invoiceStatus'; actions: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div style={{ height: 'var(--evo-toolbar-height)', padding: '0 var(--evo-space-5)', borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
        <span style={{ fontSize: 'var(--evo-fs-body-lg)', fontWeight: 'var(--evo-fw-bold)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', fontWeight: 500, flexShrink: 0 }}>
          {typeLabel}
        </span>
        <Pill tone={ESTIMATE_STATUS_TONES[status] ?? 'neutral'} size="xs">
          {t(`billing.${statusNamespace}.${status}`, { defaultValue: status })}
        </Pill>
      </div>
      <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexShrink: 0, alignItems: 'center' }}>
        {actions}
      </div>
    </div>
  );
}

// ── List panel ─────────────────────────────────────────────────────────────────
// Mirrors ReportsListPanel (pages/ReportsPage.tsx) exactly: ListToolbar for
// select-all/expand-all, TimeBandedList + per-row ListItem selection wired to
// the page-root SelectionActionBar. Both estimates AND invoices support bulk
// selection identically — same checkbox, same toolbar, same action bar. No
// per-view special-casing.

interface BillingListPanelProps {
  view: DocType;
  rows: DocRow[];
  isLoading: boolean;
  selected: { type: DocType; id: number } | null;
  onSelect: (type: DocType, id: number) => void;
  selectedIds: Set<number>;
  onToggleId: (id: number, sel: boolean) => void;
  onSelectAllVisible: (ids: number[]) => void;
  onClearSelection: () => void;
  contactNameById: Map<number, string>;
  currencyForAccount: (accountId: number) => string;
}

function BillingListPanel({ view, rows, isLoading, selected, onSelect, selectedIds, onToggleId, onSelectAllVisible, onClearSelection, contactNameById, currencyForAccount }: BillingListPanelProps) {
  const { t } = useTranslation();
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());

  return (
    <div style={{ width: 'clamp(300px, 33%, 480px)', flexShrink: 0, borderRight: '1px solid var(--evo-stroke)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ListToolbar
        allSelected={rows.length > 0 && rows.every(r => selectedIds.has(r.id))}
        someSelected={selectedIds.size > 0 && !rows.every(r => selectedIds.has(r.id))}
        onSelectAll={sel => {
          if (sel) onSelectAllVisible(rows.map(r => r.id));
          else onClearSelection();
        }}
        onExpandAll={() => setCollapsedBands(new Set())}
        onCollapseAll={() => setCollapsedBands(new Set(ALL_BANDS))}
        expandLabel={t('email.expandAll')}
        collapseLabel={t('email.collapseAll')}
      />

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading && (
          <div style={{ padding: 'var(--evo-space-4)' }}><LoadingState size="sm" /></div>
        )}
        {!isLoading && rows.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--evo-space-6) var(--evo-space-4)', textAlign: 'center' }}>
            <Icon name="activity" size={24} />
            <p style={{ fontSize: '13px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: '8px 0 0' }}>
              {view === 'estimate' ? t('billing.facturacion.noEstimates') : t('billing.facturacion.noInvoices')}
            </p>
          </div>
        )}
        {!isLoading && rows.length > 0 && (
          <TimeBandedList<DocRow>
            items={rows}
            getId={r => r.id}
            getTimestamp={r => new Date(r.createdAt).toISOString()}
            collapsedBands={collapsedBands}
            onToggleBand={band => setCollapsedBands(prev => {
              const next = new Set(prev);
              if (next.has(band)) next.delete(band); else next.add(band);
              return next;
            })}
            checkedIds={selectedIds}
            onBandCheck={() => {}}
            renderItem={row => {
              const currency = row.docType === 'estimate' ? currencyForAccount(row.accountId) : row.currency;
              const isActive = selected?.type === row.docType && selected.id === row.id;
              // Invoices: 'overdue' derivado en display (billingStatus.ts) — DB sigue 'sent'.
              const displayStatus = row.docType === 'invoice'
                ? displayInvoiceStatus({ status: row.status, dueDate: (row as Invoice).dueDate })
                : row.status;
              return (
                <ListItem
                  key={`${row.docType}-${row.id}`}
                  itemId={`${row.docType}-${row.id}`}
                  title={row.number ?? `#${row.id}`}
                  subtitle={contactNameById.get(row.contactId) ?? t('billing.facturacion.unknownClient')}
                  meta={`${formatMoney(row.total, currency)} · ${formatDate(row.createdAt)}`}
                  badges={[
                    <Pill key="status" tone={ESTIMATE_STATUS_TONES[displayStatus] ?? 'neutral'} size="xs">
                      {t(`billing.${row.docType === 'estimate' ? 'estimateStatus' : 'invoiceStatus'}.${displayStatus}`, { defaultValue: displayStatus })}
                    </Pill>,
                  ]}
                  selected={selectedIds.has(row.id)}
                  onSelect={(sel: boolean) => onToggleId(row.id, sel)}
                  active={isActive}
                  onClick={() => onSelect(row.docType, row.id)}
                  density="default"
                />
              );
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function BillingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { type: typeParam, id: idParam } = useParams<{ type?: string; id?: string }>();
  const parsedId = idParam ? parseInt(idParam) : NaN;
  const selected = (typeParam === 'estimate' || typeParam === 'invoice') && Number.isFinite(parsedId)
    ? { type: typeParam as DocType, id: parsedId }
    : null;

  const [view, setView] = useState<DocType>(selected?.type ?? 'estimate');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { data: accountsData } = useAccounts();
  const { data: contactsData } = useContacts({ limit: 500 });
  const { data: estimatesData, isLoading: estimatesLoading } = useEstimates();
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices();
  const bulkDelete = useBulkDeleteEstimates();
  const bulkDeleteInvoices = useBulkDeleteInvoices();
  const convertEstimate = useConvertEstimate();
  const { confirm, dialog: confirmDialog } = useConfirm();
  // For the detail header (title/type/status) — same queryKey EstimateDetail/
  // InvoiceDetail themselves use internally, so React Query dedupes this, no
  // extra request. Fetched by id directly (not derived via .find() over the
  // bulk list) — a .find() over invoicesData silently returns undefined
  // whenever the clicked invoice isn't in the currently-loaded list array
  // (filtered view, timing, or a stale/incomplete fetch), which is exactly
  // why the invoice detail could fail to load on click while estimates
  // (already fetched by id) never had that problem.
  const { data: selectedEstimate } = useEstimate(selected?.type === 'estimate' ? selected.id : undefined);
  const { data: selectedInvoice } = useInvoice(selected?.type === 'invoice' ? selected.id : undefined);
  // Sentinel (-1) when nothing is selected yet — same convention EstimateDetail
  // itself uses for useInvoices({ estimateId }) above; harmless for a mutation
  // since it only fires from the button's onClick, by which point
  // selectedEstimate is guaranteed loaded (button only renders when truthy).
  const composeEstimateSend = useComposeEstimateSend(selectedEstimate?.id ?? -1);
  const composeInvoiceSend = useComposeInvoiceSend(selectedInvoice?.id ?? -1);
  const composeInvoiceReminder = useComposeInvoiceReminder(selectedInvoice?.id ?? -1);
  const markInvoicePaid = useMarkInvoicePaid(selectedInvoice?.id ?? -1);
  const markEstimateAccepted = useMarkEstimateAccepted();
  // "Ver factura" on a converted estimate — same estimateId-scoped lookup
  // EstimateDetail uses internally (React Query dedupes it).
  const { data: convertedInvoicesData } = useInvoices({ estimateId: selected?.type === 'estimate' ? selected.id : -1 });
  const convertedInvoice = convertedInvoicesData?.invoices[0];

  const rows: DocRow[] = useMemo(() => {
    if (view === 'estimate') return (estimatesData?.estimates ?? []).map(e => ({ ...e, docType: 'estimate' as const }));
    return (invoicesData?.invoices ?? []).map(i => ({ ...i, docType: 'invoice' as const }));
  }, [view, estimatesData, invoicesData]);

  const contactNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of contactsData?.rows ?? []) {
      map.set(c.id, [c.firstName, c.lastName].filter(Boolean).join(' ') || c.businessName || c.primaryEmail || `#${c.id}`);
    }
    return map;
  }, [contactsData]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      const folio = (r.number ?? `#${r.id}`).toLowerCase();
      const client = (contactNameById.get(r.contactId) ?? '').toLowerCase();
      return folio.includes(q) || client.includes(q);
    });
  }, [rows, search, contactNameById]);

  function currencyForAccount(accountId: number): string {
    return accountsData?.accounts.find(a => a.id === accountId)?.currency ?? 'usd';
  }

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    setOverride({
      title: t('billing.facturacion.pageTitle'),
      heroIcon: 'facturacion-large',
      heroIconColor: 'var(--evo-steel)',
      bottomActions: (
        <PillGroup size="md">
          <Pill tone="cyan" onClick={() => setShowCreateModal(true)}>
            {t('billing.facturacion.newButton')}
          </Pill>
        </PillGroup>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t]);

  function handleSelect(type: DocType, id: number) {
    navigate(`/facturacion/${type}/${id}`);
  }

  function handleCreated(id: number) {
    setShowCreateModal(false);
    setView('estimate');
    navigate(`/facturacion/estimate/${id}`);
  }

  async function handleConvert(id: number) {
    const ok = await confirm({
      title: t('billing.facturacion.convertConfirmTitle'),
      description: t('billing.facturacion.convertConfirmDescription'),
      confirmLabel: t('billing.facturacion.convertToInvoice'),
      tone: 'cyan',
    });
    if (!ok) return;
    convertEstimate.mutate(id, {
      onSuccess: invoice => navigate(`/facturacion/invoice/${invoice.id}`),
    });
  }

  async function handleMarkPaid(_id: number) {
    const ok = await confirm({
      title: t('billing.facturacion.markPaidConfirmTitle'),
      description: t('billing.facturacion.markPaidConfirmDescription'),
      confirmLabel: t('billing.facturacion.markPaid'),
      tone: 'cyan',
    });
    if (!ok) return;
    markInvoicePaid.mutate();
  }

  function handleChangeView(v: DocType) {
    setView(v);
    setSelectedIds(new Set());
    navigate('/facturacion');
  }

  function handleToggleId(id: number, sel: boolean) {
    setSelectedIds(prev => { const next = new Set(prev); if (sel) next.add(id); else next.delete(id); return next; });
  }

  function handleSelectAllVisible(ids: number[]) {
    setSelectedIds(new Set(ids));
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          onSubmit={() => {}}
          onClear={() => setSearch('')}
          placeholder={t('billing.facturacion.searchPlaceholder')}
        />
        <div style={{ flexShrink: 0 }}>
          <SegmentedControl<DocType>
            value={view}
            onChange={handleChangeView}
            size="sm"
            options={[
              { value: 'estimate', label: t('billing.facturacion.viewEstimates') },
              { value: 'invoice', label: t('billing.facturacion.viewInvoices') },
            ]}
          />
        </div>
      </FilterRow>

      <SelectionActionBar
        visible={selectedIds.size > 0}
        countLabel={t('billing.facturacion.selected', { count: selectedIds.size })}
        onClearSelection={clearSelection}
      >
        <SelectionActionBar.Action
          label={t('billing.facturacion.bulkDelete')}
          tone="danger"
          confirmRequired
          loading={view === 'estimate' ? bulkDelete.isPending : bulkDeleteInvoices.isPending}
          onClick={() => {
            const ids = Array.from(selectedIds);
            const mutation = view === 'estimate' ? bulkDelete : bulkDeleteInvoices;
            mutation.mutate(ids, {
              onSuccess: () => {
                if (selected?.type === view && selectedIds.has(selected.id)) navigate('/facturacion');
                clearSelection();
              },
            });
          }}
        />
      </SelectionActionBar>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <BillingListPanel
          view={view}
          rows={filteredRows}
          isLoading={view === 'estimate' ? estimatesLoading : invoicesLoading}
          selected={selected}
          onSelect={handleSelect}
          selectedIds={selectedIds}
          onToggleId={handleToggleId}
          onSelectAllVisible={handleSelectAllVisible}
          onClearSelection={clearSelection}
          contactNameById={contactNameById}
          currencyForAccount={currencyForAccount}
        />

        {selected ? (
          // Mirrors DetailPanel's outer wrapper (ReportsPage.tsx) exactly —
          // flex:1 + minWidth:0 so this column actually claims the space
          // between the list and the fixed-width assistant panel, instead
          // of sizing to content and displacing the assistant. DetailHeader
          // + the 20px 24px scroll-body padding below are Reports' own
          // action-row/detail-body markup, copied exactly.
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            {selected.type === 'estimate' ? (
              selectedEstimate && (
                <DetailHeader
                  title={`${selectedEstimate.number ?? `#${selectedEstimate.id}`} — ${contactNameById.get(selectedEstimate.contactId) ?? t('billing.facturacion.unknownClient')}`}
                  typeLabel={t('billing.facturacion.typeEstimate')}
                  status={selectedEstimate.status}
                  actions={
                    // Matriz por status (Part D). Las acciones GUÍAN el paso
                    // siguiente, nunca bloquean: convertir no requiere enviar,
                    // aceptar no es prerequisito de convertir.
                    <>
                      {(selectedEstimate.status === 'draft' || selectedEstimate.status === 'sent' || selectedEstimate.status === 'accepted') && (
                        <Button
                          size="sm"
                          variant="outline"
                          tone="cyan"
                          loading={composeEstimateSend.isPending}
                          onClick={() => composeEstimateSend.mutate()}
                        >
                          {selectedEstimate.status === 'draft'
                            ? t('billing.facturacion.sendToClient')
                            : t('billing.facturacion.resendToClient')}
                        </Button>
                      )}
                      {selectedEstimate.status === 'sent' && (
                        <Button
                          size="sm"
                          variant="outline"
                          tone="cyan"
                          loading={markEstimateAccepted.isPending}
                          onClick={() => markEstimateAccepted.mutate(selectedEstimate.id)}
                        >
                          {t('billing.facturacion.markAccepted')}
                        </Button>
                      )}
                      {selectedEstimate.status !== 'converted' && (
                        <Button
                          size="sm"
                          variant="outline"
                          tone="cyan"
                          loading={convertEstimate.isPending}
                          onClick={() => { void handleConvert(selectedEstimate.id); }}
                        >
                          {t('billing.facturacion.convertToInvoice')}
                        </Button>
                      )}
                      {selectedEstimate.status === 'converted' && convertedInvoice && (
                        <Button
                          size="sm"
                          variant="outline"
                          tone="cyan"
                          onClick={() => navigate(`/facturacion/invoice/${convertedInvoice.id}`)}
                        >
                          {t('billing.facturacion.viewInvoice')}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" tone="muted" onClick={() => window.open(`/api/billing/estimates/${selectedEstimate.id}/preview`, '_blank')}>
                        {t('billing.estimates.preview')}
                      </Button>
                    </>
                  }
                />
              )
            ) : (
              selectedInvoice && (
                <DetailHeader
                  title={`${selectedInvoice.number ?? `#${selectedInvoice.id}`} — ${contactNameById.get(selectedInvoice.contactId) ?? t('billing.facturacion.unknownClient')}`}
                  typeLabel={t('billing.facturacion.typeInvoice')}
                  status={displayInvoiceStatus(selectedInvoice)}
                  statusNamespace="invoiceStatus"
                  actions={
                    // Matriz por status (Part D). 'overdue' es estado derivado
                    // de display (billingStatus.ts) — la DB sigue en 'sent',
                    // por eso las condiciones chequean el status real.
                    <>
                      {selectedInvoice.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          tone="cyan"
                          loading={composeInvoiceSend.isPending}
                          onClick={() => composeInvoiceSend.mutate()}
                        >
                          {t('billing.facturacion.sendToClient')}
                        </Button>
                      )}
                      {selectedInvoice.status === 'sent' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            tone="cyan"
                            loading={composeInvoiceSend.isPending}
                            onClick={() => composeInvoiceSend.mutate()}
                          >
                            {t('billing.facturacion.resendToClient')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            tone="cyan"
                            loading={composeInvoiceReminder.isPending}
                            onClick={() => composeInvoiceReminder.mutate()}
                          >
                            {t('billing.facturacion.paymentReminder')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            tone="cyan"
                            loading={markInvoicePaid.isPending}
                            onClick={() => { void handleMarkPaid(selectedInvoice.id); }}
                          >
                            {t('billing.facturacion.markPaid')}
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" tone="muted" onClick={() => window.open(`/api/billing/invoices/${selectedInvoice.id}/preview`, '_blank')}>
                        {t('billing.estimates.preview')}
                      </Button>
                    </>
                  }
                />
              )
            )}
            <div style={{ flex: 1, overflow: 'auto', padding: '20px var(--evo-space-5)' }}>
              {selected.type === 'estimate' ? (
                <EstimateDetail
                  id={selected.id}
                  accounts={accountsData?.accounts ?? []}
                />
              ) : (
                <InvoiceDetail id={selected.id} />
              )}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', color: 'var(--evo-fg-3)', minWidth: 0 }}>
            <Icon name="activity" size={28} />
            <p style={{ fontSize: '13px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', margin: 0 }}>{t('billing.facturacion.selectDocument')}</p>
          </div>
        )}

        <BillingClaudePanel docType={selected?.type ?? null} docId={selected?.id ?? null} />
      </div>

      {showCreateModal && (
        <CreateEstimateModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
      {confirmDialog}
    </div>
  );
}
